import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { MenuItem } from '@/features/menu/components/Menu/Menu';
import type { MenuLocale } from '@/features/menu/constants/menuLabels';
import { Menu } from '@/features/menu/components/Menu/Menu';
import { MenuSidebar } from '@/features/menu/components/Sidebar/MenuSidebar';
import { SearchSelector } from '@/features/menu/components/SearchSelector/SearchSelector';
import { DataCollected } from '@/features/menu/components/DataCollected/DataCollected';
import { getMenuItems } from '@/features/menu/constants/menuItems';
import { BrandLogo } from '@/core/components/BrandLogo/BrandLogo';
import { getAccountId, getUniId } from '@/core/utils/extractCookieId';

interface MenuPageProps {
  itemIds?: string[];
  disabledItemIds?: Array<string | { id: string; reason?: 'upgrade' | 'permission' }>;
  accountId?: string;
  accountName?: string;
  profileImage?: string;
  searchOptions?: Array<string | { label?: string; name?: string; universeId?: string; id?: string; value?: string }>;
  selectedSearch?: string;
  universeId?: string;
  defaultActive?: string;
}

const applyDisabledItems = (
  items: MenuItem[],
  disabledItemIds?: Array<string | { id: string; reason?: 'upgrade' | 'permission' }>
): MenuItem[] => {
  if (!disabledItemIds || disabledItemIds.length === 0) {
    return items;
  }

  const disabledMap = new Map<string, 'upgrade' | 'permission' | undefined>();
  disabledItemIds.forEach((entry) => {
    if (typeof entry === 'string') {
      disabledMap.set(entry, undefined);
    } else if (entry?.id) {
      disabledMap.set(entry.id, entry.reason);
    }
  });

  return items.map((item) => {
    const isExternalDisabled = disabledMap.has(item.id);
    const reason = disabledMap.get(item.id);

    return {
      ...item,
      disabled: item.disabled ?? isExternalDisabled,
      disabledReason: item.disabledReason ?? reason,
      children: item.children ? applyDisabledItems(item.children, disabledItemIds) : item.children,
    };
  });
};

const resolveIdValue = (value?: string, fallback?: string): string => {
  if (value && value.trim() !== '') {
    return value;
  }

  return fallback === 'DEFAULT' ? '' : (fallback ?? '');
};

export const MenuPage: React.FC<MenuPageProps> = ({
  itemIds,
  disabledItemIds,
  accountId,
  accountName,
  profileImage,
  searchOptions,
  selectedSearch,
  universeId,
}) => {
  const { t, i18n } = useTranslation();

  const resolvedItemIds = itemIds;
  const resolvedDisabledItemIds = disabledItemIds;

  const resolvedAccountID = resolveIdValue(accountId, getAccountId());
  const resolvedUniverseID = resolveIdValue(universeId, getUniId());

  const menuItems = useMemo(() => {
    const resolvedLocale = (i18n.language as MenuLocale) || 'pt';
    const rawItems = getMenuItems(resolvedLocale, resolvedItemIds, resolvedAccountID, resolvedUniverseID);
    return applyDisabledItems(rawItems, resolvedDisabledItemIds);
  }, [i18n.language, resolvedItemIds, resolvedAccountID, resolvedUniverseID, resolvedDisabledItemIds]);

  const handleNavigate = () => undefined;

  const labels = (t('menu', { returnObjects: true }) || {}) as Record<string, string>;

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        width: '240px',
        overflowX: 'hidden',
      }}
    >
      <MenuSidebar
        logoSlot={
          <BrandLogo
            className="menu-sidebar__logo-brand"
            illustrationWrapperClassName="menu-sidebar__logo-brand-blip"
            illustrationClassName="menu-sidebar__logo-brand-icon"
            altText={t('common.brandIcon')}
          />
        }
        searchSelectorSlot={
          <SearchSelector
            profileName={accountName}
            avatarUrl={profileImage}
            searchOptions={searchOptions}
            selectedSearch={selectedSearch}
            universeId={universeId}
          />
        }
        dataCollectedSlot={
          <DataCollected 
            labels={{
              title: labels.dataCollectedTitle,
              limit: labels.dataCollectedLimit,
              listening: labels.dataCollectedListening,
              used: labels.dataCollectedUsed,
              unlimited: labels.dataCollectedUnlimited,
              monthlySuffix: labels.dataCollectedMonthlySuffix,
              tooltipListening: labels.dataCollectedTooltipListening,
              tooltipCollectedData: labels.dataCollectedTooltipCollectedData,
            }}
          />
        }
        menuSlot={<Menu items={menuItems} onNavigate={handleNavigate} />}
      />
    </div>
  );
};
